import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Users, Search, X, Building2, User, Phone, Mail, ChevronDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable Recurring Client Selector
 * Usage: <RecurringClientSelector onSelect={(client) => {...}} onClear={() => {...}} />
 */
const RecurringClientSelector = ({ 
  onSelect, 
  onClear, 
  selectedClient = null,
  placeholder = "Search recurring clients...",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch clients on search
  useEffect(() => {
    const fetchClients = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/api/admin/recurring-clients/search`, {
          params: { q: searchQuery }
        });
        setClients(response.data || []);
      } catch (error) {
        console.error('Error fetching clients:', error);
        setClients([]);
      }
      setLoading(false);
    };

    const debounce = setTimeout(fetchClients, 200);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (client) => {
    onSelect(client);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = () => {
    onClear && onClear();
    setSearchQuery('');
  };

  const formatClientDisplay = (client) => {
    const name = `${client.first_name} ${client.last_name}`.trim();
    if (client.company) {
      return `${client.company} - ${name}`;
    }
    return name;
  };

  // If a client is selected, show selected state
  if (selectedClient) {
    return (
      <div className={`relative ${className}`}>
        <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <Users className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-blue-900 truncate">
              {formatClientDisplay(selectedClient)}
            </div>
            <div className="text-xs text-blue-600 flex items-center gap-3">
              {selectedClient.email && (
                <span className="flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  {selectedClient.email}
                </span>
              )}
              {selectedClient.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {selectedClient.phone}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="p-1 hover:bg-blue-100 rounded-full transition-colors"
            data-testid="clear-recurring-client"
          >
            <X className="w-4 h-4 text-blue-600" />
          </button>
        </div>
        <div className="mt-1 text-xs text-blue-600 flex items-center gap-1">
          <Users className="w-3 h-3" />
          Recurring Client Selected
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Search Input */}
      <div 
        className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg cursor-pointer hover:border-blue-300 transition-colors bg-white"
        onClick={() => {
          setIsOpen(true);
          setTimeout(() => inputRef.current?.focus(), 100);
        }}
        data-testid="recurring-client-selector"
      >
        <Users className="w-5 h-5 text-gray-400" />
        {isOpen ? (
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={placeholder}
            className="flex-1 outline-none text-sm bg-transparent"
            onClick={(e) => e.stopPropagation()}
            data-testid="recurring-client-search-input"
          />
        ) : (
          <span className="flex-1 text-sm text-gray-500">{placeholder}</span>
        )}
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
              Searching...
            </div>
          ) : clients.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              {searchQuery ? 'No clients found' : 'No recurring clients yet'}
            </div>
          ) : (
            clients.map((client) => (
              <div
                key={client.id}
                onClick={() => handleSelect(client)}
                className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                data-testid={`recurring-client-option-${client.id}`}
              >
                <div className="flex items-center gap-2">
                  {client.company ? (
                    <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  ) : (
                    <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {formatClientDisplay(client)}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-3">
                      {client.email && (
                        <span className="truncate">{client.email}</span>
                      )}
                      {client.phone && (
                        <span>{client.phone}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default RecurringClientSelector;
